interface SurveyPdfMeta {
  filename: string
  heading: string
  subheading?: string
}

const collapse = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim()

const isFormControl = (el: Element) =>
  el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA'

const isHidden = (el: HTMLElement) => {
  if (el.classList.contains('no-print')) return true
  const style = window.getComputedStyle(el)
  return style.display === 'none' || style.visibility === 'hidden'
}

// Best-effort question text for a field that has no wrapping <label>.
const labelForControl = (
  el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
): string => {
  const inCheck = el.closest('.form-check')
  if (inCheck) {
    const lbl = inCheck.querySelector('label')
    if (lbl?.textContent?.trim()) return collapse(lbl.textContent)
  }

  const parentLabel =
    el.parentElement?.querySelector(':scope > label') || el.parentElement?.querySelector('label')
  if (parentLabel?.textContent?.trim()) return collapse(parentLabel.textContent)

  const id = el.getAttribute('id')
  if (id) {
    const forLabel = el.ownerDocument.querySelector(
      `label[for="${window.CSS && CSS.escape ? CSS.escape(id) : id}"]`
    )
    if (forLabel?.textContent?.trim()) return collapse(forLabel.textContent)
  }

  return collapse(
    el.getAttribute('placeholder') ||
      el.getAttribute('aria-label') ||
      el.getAttribute('name') ||
      id ||
      ''
  )
}

export async function downloadSurveyPdf(root: HTMLElement, meta: SurveyPdfMeta) {
  const { default: jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 15
  const contentW = pageW - margin * 2
  let y = margin

  const room = (needed: number) => {
    if (y + needed > pageH - margin) {
      doc.addPage()
      y = margin
    }
  }

  const write = (
    text: string,
    opts: { size?: number; bold?: boolean; indent?: number; gap?: number; color?: number } = {}
  ) => {
    const { size = 10, bold = false, indent = 0, gap = 2, color = 30 } = opts
    const trimmed = collapse(text)
    if (!trimmed) return
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(size)
    doc.setTextColor(color)
    const lineHeight = size * 0.45 + 1
    // jsPDF never splits a single line across pages, so sentences stay intact.
    for (const line of doc.splitTextToSize(trimmed, contentW - indent) as string[]) {
      room(lineHeight)
      doc.text(line, margin + indent, y)
      y += lineHeight
    }
    y += gap
  }

  write(meta.heading, { size: 15, bold: true, gap: 1 })
  if (meta.subheading) write(meta.subheading, { size: 10, color: 90, gap: 1 })
  write(new Date().toLocaleString('de-DE'), { size: 8, color: 120, gap: 3 })
  room(4)
  doc.setDrawColor(200)
  doc.line(margin, y, pageW - margin, y)
  y += 5

  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      const el = child as HTMLElement

      if (isHidden(el)) continue
      if (el.tagName === 'BUTTON' || el.classList.contains('btn')) continue

      if (/^H[1-6]$/.test(el.tagName)) {
        const level = Number(el.tagName[1])
        y += level <= 4 ? 3 : 1.5
        write(el.textContent || '', { size: level <= 4 ? 12 : 10.5, bold: true, gap: 1.5 })
        continue
      }

      if (el.tagName === 'B' || el.tagName === 'STRONG') {
        if (!el.querySelector('input, select, textarea')) {
          write(el.textContent || '', { size: 10.5, bold: true, gap: 1.5 })
          continue
        }
      }

      // Standalone instruction / group-question text.
      if ((el.tagName === 'P' || el.tagName === 'LI') && el.textContent?.trim()) {
        if (!el.querySelector('input, select, textarea, button')) {
          write(el.textContent, { size: 10, indent: el.tagName === 'LI' ? 6 : 2, gap: 1 })
          continue
        }
      }

      if (el.tagName === 'LABEL') {
        const forId = el.getAttribute('for')
        const points = forId ? el.ownerDocument.getElementById(forId) : null
        if (el.closest('.form-check') || (points && isFormControl(points))) continue
        write(el.textContent || '', { size: 10, indent: 2, gap: 1 })
        continue
      }

      if (el.tagName === 'INPUT') {
        const input = el as HTMLInputElement
        const type = input.type
        if (['hidden', 'file', 'submit', 'button', 'image', 'reset'].includes(type)) continue

        const label = labelForControl(input)
        if (type === 'checkbox') {
          write(`${input.checked ? '[X]' : '[ ]'}  ${label || '—'}`, { indent: 4, gap: 1 })
        } else if (type === 'radio') {
          if (input.checked)
            write(`•  ${label || collapse(input.value) || '—'}`, { indent: 4, gap: 1 })
        } else {
          const value = collapse(input.value)
          if (label || value) write(`${label || '—'}: ${value || '—'}`, { indent: 4, gap: 1 })
        }
        continue
      }

      if (el.tagName === 'TEXTAREA') {
        const ta = el as HTMLTextAreaElement
        write(`${labelForControl(ta) || '—'}:`, { indent: 4, gap: 0.5 })
        write(collapse(ta.value) || '—', { indent: 8, color: 70, gap: 1.5 })
        continue
      }

      if (el.tagName === 'SELECT') {
        const sel = el as HTMLSelectElement
        const chosen = collapse(sel.selectedOptions[0]?.text)
        write(`${labelForControl(sel) || '—'}: ${chosen || '—'}`, { indent: 4, gap: 1 })
        continue
      }

      // Generic container (e.g. AML result rows built from <div><b>Label</b>: value</div>):
      // if nothing structural is nested inside, treat it as one text leaf instead of
      // silently losing the plain-text parts that sit next to inline tags like <b>/<span>.
      if (
        !el.querySelector(
          'div, details, summary, table, ul, ol, li, input, select, textarea, button, h1, h2, h3, h4, h5, h6'
        )
      ) {
        write(el.textContent || '', { size: 10, indent: 2, gap: 1 })
        continue
      }

      if (el.children.length) walk(el)
    }
  }

  walk(root)

  doc.save(meta.filename.toLowerCase().endsWith('.pdf') ? meta.filename : `${meta.filename}.pdf`)
}
